import { type CreatePriceSessionResponse, UploadType } from '@/api';
import PageHeader, {
  type PageHeaderProps,
} from '@/components/page-header/page-header';
import { CREATE_PRICE_PUBLISHED_ROWS_STORAGE_KEY } from '@/pages/price-planning/create-price-page/create-price-constants';
import { sessionQueryParametersToFilters } from '@/pages/price-planning/create-price-page/create-price-helpers';
import {
  useDeleteCreatePriceSession,
  useFetchCreatePriceSession,
} from '@/pages/price-planning/create-price-page/create-price-queries';
import {
  CreatePriceMode,
  encodeCreatePriceParams,
} from '@/pages/price-planning/create-price-page/create-price-search-params';
import { ROUTES } from '@/routes';
import { themeColors } from '@/theme/colors';
import { Container } from '@lego/klik-ui';
import { type PropsWithChildren, useCallback, useRef, useState } from 'react';
import { generatePath, useNavigate } from 'react-router-dom';

import CreatePriceModal from '@/pages/price-planning/create-price-page/components/create-price-modal';
import { SessionRecoveryModal } from '@/pages/price-planning/create-price-page/components/session-recovery-modal';
import DownloadMismatchesModal from './components/global-modals/download-mismatches-modal';
import FlowhavenModal from './components/global-modals/flowhaven-modal';
import ImportSimulationFilesModal from './components/global-modals/import-simulation-files-modal';
import RunAutoCalcModal from './components/global-modals/run-auto-calc-modal';
import UploadModal from './components/global-modals/upload-modal';
import { GLOBAL_MODAL, MainNavbar } from './components/main-navbar';
import { DEFAULT_PAGE_MAX_WIDTH, type PageMaxWidth } from './page-layout';

interface PageWrapperProps extends PropsWithChildren {
  headerProps: PageHeaderProps;
  maxWidth?: PageMaxWidth;
  contentPaddingX?: number | string;
  layout?: 'scroll' | 'fill';
  backgroundColor?: string;
  stickyNavbar?: boolean;
}

export const PageWrapper = ({
  maxWidth = DEFAULT_PAGE_MAX_WIDTH,
  contentPaddingX = '16px',
  layout = 'scroll',
  stickyNavbar = true,
  backgroundColor = themeColors.surface.background.base.muted,
  headerProps,
  children,
}: PageWrapperProps) => {
  const isFill = layout === 'fill';
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState<GLOBAL_MODAL | null>(null);
  const [sessionData, setSessionData] =
    useState<CreatePriceSessionResponse | null>(null);
  const [showRecoveryModal, setShowRecoveryModal] = useState(false);
  const [selectedCreatePriceMode, setSelectedCreatePriceMode] =
    useState<CreatePriceMode>(CreatePriceMode.Create);
  const { mutateAsync: fetchSession } = useFetchCreatePriceSession();
  const { mutate: deleteSession, isPending: isDiscarding } =
    useDeleteCreatePriceSession(selectedCreatePriceMode);

  const flowhavenTriggerRef = useRef<HTMLElement | null>(null);
  const runAutoCalcTriggerRef = useRef<HTMLElement | null>(null);
  const createPriceTriggerRef = useRef<HTMLElement | null>(null);
  const uploadPriceTriggerRef = useRef<HTMLElement | null>(null);
  const uploadCostTriggerRef = useRef<HTMLElement | null>(null);
  const uploadValueBasedPriceTriggerRef = useRef<HTMLElement | null>(null);
  const importSimulationFilesTriggerRef = useRef<HTMLElement | null>(null);
  const downloadMismatchesTriggerRef = useRef<HTMLElement | null>(null);
  const sessionRecoveryTriggerRef = useRef<HTMLElement | null>(null);

  const handleCreatePriceNavClick = useCallback(
    async (modal: GLOBAL_MODAL) => {
      const targetMode =
        modal === GLOBAL_MODAL.ApprovePrice
          ? CreatePriceMode.Approve
          : CreatePriceMode.Create;

      setSelectedCreatePriceMode(targetMode);
      sessionStorage.removeItem(CREATE_PRICE_PUBLISHED_ROWS_STORAGE_KEY);

      try {
        const session = await fetchSession(targetMode);
        if (session && (session.mode as CreatePriceMode) === targetMode) {
          setSessionData(session);
          setShowRecoveryModal(true);
          return;
        }
      } catch (e) {
        console.warn('Failed to fetch create price session:', e);
      }

      setShowModal(modal);
    },
    [fetchSession],
  );

  const handleNavbarModalTrigger = useCallback(
    (modal: GLOBAL_MODAL) => {
      if (
        modal === GLOBAL_MODAL.CreatePrice ||
        modal === GLOBAL_MODAL.ApprovePrice
      ) {
        handleCreatePriceNavClick(modal);
      } else {
        setShowModal(modal);
      }
    },
    [handleCreatePriceNavClick],
  );

  const closeGlobalModal = useCallback(() => {
    setShowModal(null);
  }, []);

  const handleSessionContinue = useCallback(() => {
    if (!sessionData) return;

    const filters = sessionQueryParametersToFilters(
      sessionData._queryParameters,
    );
    const mode = sessionData.mode as CreatePriceMode;
    const searchParams = encodeCreatePriceParams(filters, mode, {
      fromSession: true,
    });

    setShowRecoveryModal(false);
    setSessionData(null);

    navigate({
      pathname: generatePath(ROUTES.CreatePrice.path),
      search: searchParams.toString(),
    });
  }, [sessionData, navigate]);

  const handleSessionDiscard = useCallback(() => {
    deleteSession(undefined, {
      onError: (error) => {
        console.warn('Failed to delete create price session:', error);
      },
      onSettled: () => {
        setShowRecoveryModal(false);
        setSessionData(null);

        const modal =
          selectedCreatePriceMode === CreatePriceMode.Approve
            ? GLOBAL_MODAL.ApprovePrice
            : GLOBAL_MODAL.CreatePrice;
        setShowModal(modal);
      },
    });
  }, [deleteSession, selectedCreatePriceMode]);

  const isCreatePriceModalOpen =
    showModal === GLOBAL_MODAL.CreatePrice ||
    showModal === GLOBAL_MODAL.ApprovePrice;

  return (
    <Container
      maxW="100%"
      height={isFill ? '100vh' : 'auto'}
      minHeight="100vh"
      padding={0}
      display="flex"
      backgroundColor={backgroundColor}
      flexDirection="column"
    >
      <MainNavbar
        sticky={stickyNavbar}
        setShowModal={handleNavbarModalTrigger}
        onFlowhavenTriggerClick={(triggerElement) => {
          flowhavenTriggerRef.current = triggerElement;
        }}
        onRunAutoCalcTriggerClick={(triggerElement) => {
          runAutoCalcTriggerRef.current = triggerElement;
        }}
        onCreatePriceTriggerClick={(triggerElement) => {
          createPriceTriggerRef.current = triggerElement;
        }}
        onUploadPriceTriggerClick={(triggerElement) => {
          uploadPriceTriggerRef.current = triggerElement;
        }}
        onUploadCostTriggerClick={(triggerElement) => {
          uploadCostTriggerRef.current = triggerElement;
        }}
        onUploadValueBasedPriceTriggerClick={(triggerElement) => {
          uploadValueBasedPriceTriggerRef.current = triggerElement;
        }}
        onImportSimulationFilesTriggerClick={(triggerElement) => {
          importSimulationFilesTriggerRef.current = triggerElement;
        }}
        onDownloadMismatchesTriggerClick={(triggerElement) => {
          downloadMismatchesTriggerRef.current = triggerElement;
        }}
      />
      <PageHeader {...headerProps} />
      <Container
        maxW="100%"
        flexGrow={isFill ? 1 : 0}
        paddingX={contentPaddingX}
        display="flex"
        flexDirection="column"
        minHeight={isFill ? 0 : 'auto'}
      >
        <Container
          flex={isFill ? 1 : 0}
          padding={0}
          maxW={maxWidth}
          width="100%"
          margin="0 auto"
          overflow={isFill ? 'auto' : 'visible'}
          minHeight={isFill ? 0 : 'auto'}
          position="relative"
        >
          {children}
        </Container>
      </Container>
      <FlowhavenModal
        isOpen={showModal === GLOBAL_MODAL.Flowhaven}
        triggerRef={flowhavenTriggerRef}
        onClose={closeGlobalModal}
      />
      <RunAutoCalcModal
        isOpen={showModal === GLOBAL_MODAL.RunAutoCalc}
        triggerRef={runAutoCalcTriggerRef}
        onClose={closeGlobalModal}
      />
      <CreatePriceModal
        key={showModal === GLOBAL_MODAL.ApprovePrice ? 'approve' : 'create'}
        isOpen={isCreatePriceModalOpen}
        triggerRef={createPriceTriggerRef}
        onClose={closeGlobalModal}
        mode={
          showModal === GLOBAL_MODAL.ApprovePrice
            ? CreatePriceMode.Approve
            : CreatePriceMode.Create
        }
      />
      <UploadModal
        isOpen={showModal === GLOBAL_MODAL.UploadPrice}
        triggerRef={uploadPriceTriggerRef}
        type={UploadType.Rrp}
        onClose={closeGlobalModal}
      />
      <UploadModal
        isOpen={showModal === GLOBAL_MODAL.UploadCost}
        triggerRef={uploadCostTriggerRef}
        type={UploadType.Cost}
        onClose={closeGlobalModal}
      />
      <UploadModal
        isOpen={showModal === GLOBAL_MODAL.UploadValueBasedPrice}
        triggerRef={uploadValueBasedPriceTriggerRef}
        type={UploadType.ValueBasedPrice}
        onClose={() => setShowModal(null)}
      />
      <ImportSimulationFilesModal
        isOpen={showModal === GLOBAL_MODAL.ImportSimulationFiles}
        triggerRef={importSimulationFilesTriggerRef}
        onClose={closeGlobalModal}
      />
      <DownloadMismatchesModal
        isOpen={showModal === GLOBAL_MODAL.DownloadMismatches}
        triggerRef={downloadMismatchesTriggerRef}
        onClose={closeGlobalModal}
      />
      {sessionData && (
        <SessionRecoveryModal
          isOpen={showRecoveryModal}
          triggerRef={sessionRecoveryTriggerRef}
          onClose={() => {
            setShowRecoveryModal(false);
            setSessionData(null);
          }}
          onContinue={handleSessionContinue}
          onDiscard={handleSessionDiscard}
          isDiscarding={isDiscarding}
          session={sessionData}
        />
      )}
    </Container>
  );
};
